import { useQuery } from '@tanstack/react-query';

export interface LinkPreviewData {
  title: string | null;
  description: string | null;
  image: string | null;
  logo: string | null;
  publisher: string | null;
  url: string;
}

// Allowed domains for link previews to prevent SSRF attacks
const ALLOWED_PREVIEW_DOMAINS = [
  'linkedin.com',
  'www.linkedin.com',
  'twitter.com',
  'x.com',
  'github.com',
  'medium.com',
  'substack.com',
];

function isAllowedUrl(urlString: string): boolean {
  try {
    const parsed = new URL(urlString);
    // Only allow HTTPS URLs
    if (parsed.protocol !== 'https:') return false;
    // Check if domain is in allowlist (or subdomain of allowed domain)
    const hostname = parsed.hostname.toLowerCase();
    return ALLOWED_PREVIEW_DOMAINS.some(
      (domain) => hostname === domain || hostname.endsWith('.' + domain)
    );
  } catch {
    return false;
  }
}

export function useLinkPreview(url: string | null) {
  return useQuery({
    queryKey: ['link-preview', url],
    queryFn: async (): Promise<LinkPreviewData | null> => {
      if (!url) return null;

      // Validate URL to prevent SSRF - only allow specific public domains
      if (!isAllowedUrl(url)) {
        console.warn('Link preview blocked: URL not in allowlist');
        return null;
      }

      try {
        const response = await fetch(
          `https://api.microlink.io/?url=${encodeURIComponent(url)}`
        );

        // Check for HTTP errors
        if (!response.ok) {
          console.warn(`Link preview fetch failed: ${response.status}`);
          return null;
        }

        const json = await response.json();
        if (json.status !== 'success' || !json.data) return null;

        return {
          title: json.data.title || null,
          description: json.data.description || null,
          image: json.data.image?.url || null,
          logo: json.data.logo?.url || null,
          publisher: json.data.publisher || null,
          url: json.data.url || url,
        };
      } catch (error) {
        console.warn('Link preview error:', error);
        return null;
      }
    },
    enabled: !!url,
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24 * 7,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}
